/* lib/api/subscription.js — порт боевого клиента подписки (frontend/src/lib/api/subscription.js, 1:1).
 * Каждый метод принимает опц. workspaceId → явный X-Workspace-Id (бизнес-секция читает чужой ws
 * без переключения активного). Контракт {ok,status,data}. Числа (price_rub/amount_rub) — строки DRF. */

import { QUIET_POLL, apiFetch } from './client.js';
import { consentBody, planChangeConsentBody } from '../recurringConsent.js';

function wsHeaders(workspaceId) {
  if (workspaceId == null) return undefined;
  return { 'X-Workspace-Id': String(workspaceId) };
}

export const getSubscription = (workspaceId) =>
  apiFetch('/api/subscription/me/', { headers: wsHeaders(workspaceId) });

export const listPlans = () => apiFetch('/api/subscription/plans/');

// Публичная ссылка покупки (/buy/<слаг>): карточка тарифа без входа.
export const getOffer = (slug) =>
  apiFetch(`/api/subscription/offer/${encodeURIComponent(slug)}/`);

export const listPayments = (workspaceId) =>
  apiFetch('/api/subscription/payments/', { headers: wsHeaders(workspaceId) });

// опрос только на чтение: по умолчанию вне счёта запросов в полёте (QUIET_POLL) — не держит и
// не сбрасывает переход вкладки в пространство, выбранное в соседней; req — переопределить
export const getPaymentStatus = (invoiceId, workspaceId, req) =>
  apiFetch(`/api/subscription/payments/${encodeURIComponent(invoiceId)}/`, { headers: wsHeaders(workspaceId), ...QUIET_POLL, ...req });

// method: 'card' (дефолт, можно не передавать) | 'foreign_card' — оплата из-за
// рубежа иностранной картой (нерекуррентно, за флагом robokassa_foreign_enabled).
// consent: true — плательщик поставил галочку согласия на автосписания
// (RecurringConsentModal). Без неё сервер не создаст платёж, который привяжет
// карту (400 recurring_consent_required); инокарте и разовым тарифам она не нужна.
export const startCheckout = (plan_slug, workspaceId, method, consent) =>
  apiFetch('/api/subscription/checkout/', {
    method: 'POST',
    body: {
      plan_slug,
      ...(method && method !== 'card' ? { method } : {}),
      ...consentBody(consent),
    },
    headers: wsHeaders(workspaceId),
  });

// Онбординг-CJM (контракт C4): посадить новое пространство на бесплатный тариф
// без оплаты — демо-период либо персональный тариф владельца.
// POST /trial/ {workspace_id} → всегда 200
// {status:'activated'|'already'|'paid_plan'|'demo_used'|'granted', expires_at}.
// 'demo_used' — владелец уже израсходовал свой единственный «Быстрый старт»:
// период НЕ включён, expires_at = null.
// 'granted' — у владельца есть выданный персональный тариф (`personal_plan` в
// /me/): пространство посажено на него и ОТКРЫТО. expires_at = null — выдача
// бессрочная, иначе срок доступа. Какой из двух путей сработает, решает сервер:
// экран свой выбор не присылает и верит статусу.
// ⚠️ 'already' может нести ПРОШЕДШИЙ expires_at (период уже использован ранее) —
// вызывающий обязан сверять дату и не рисовать такой ответ как активный период.
// Эндпоинт идемпотентен (повтор после удачной активации вернёт 'already', после
// 'granted' — снова 'granted'), поэтому мастер создания повторяет его при сбое —
// screens/settings/quickStart.js.
export const startTrial = (workspace_id) =>
  apiFetch('/api/subscription/trial/', { method: 'POST', body: { workspace_id } });

// Понижение: тариф дешевле включится оплатой продления по его цене. consent=true —
// человек поставил галочку согласия на списания по новой цене (нужно, когда
// следующий период спишут с карты; без него сервер ответит recurring_consent_required).
// expectedPriceRub — цена из окна согласия: сменилась на сервере → 409 price_changed
// {price_rub, expected_price_rub}, ничего не записано.
export const changePlan = (plan_slug, workspaceId, consent, expectedPriceRub) =>
  apiFetch('/api/subscription/change/', {
    method: 'POST',
    body: { plan_slug, ...planChangeConsentBody(consent, expectedPriceRub) },
    headers: wsHeaders(workspaceId),
  });

// «Понял, беру ассистента» — больше не переспрашиваем этого человека.
export const dismissPersonalWarning = () =>
  apiFetch('/api/subscription/dismiss-personal-warning/', { method: 'POST', body: {} });

export const cancelPendingChange = (workspaceId) =>
  apiFetch('/api/subscription/cancel-pending/', { method: 'POST', body: {}, headers: wsHeaders(workspaceId) });

// Вкл/выкл автопродление текущей подписки без смены плана.
// POST /auto-renew/ {enabled} → payload подписки. enabled:true: разовый тариф (акция),
// разовая продажа (ссылка без продления, инокарта) или оплаченная акция в очереди → 409
// not_renewable (текст — в detail); карта или безнал (счёт Точки — продление без карты)
// пускают; ни того, ни другого (канал manual) → 400 no_recurring. У выключения при ждущем
// счёте на продление — ещё `notice`.
export const setAutoRenew = (enabled, workspaceId) =>
  apiFetch('/api/subscription/auto-renew/', { method: 'POST', body: { enabled }, headers: wsHeaders(workspaceId) });

// Стаб боевого ResultURL Робокассы (только в DEBUG).
export const mockPaymentCallback = (payment_id, outcome = 'success') =>
  apiFetch('/api/subscription/mock/callback/', { method: 'POST', body: { payment_id, outcome } });

/* Безналичная оплата по счёту (банк Точка) — только бизнес-тарифы, за фиче-флагом.
   Тот же принцип workspaceId → явный X-Workspace-Id: счёт выставляется на бизнес-ws,
   который может быть не активным. Контракт эндпоинтов — заморожен (см. tochka-invoicing). */

// POST /invoice/checkout/ → 201 {payment_id, invoice_id, document_id, number, amount_rub, status, pdf_url}
export const startInvoiceCheckout = (plan_slug, requisites, offer_accepted, workspaceId) =>
  apiFetch('/api/subscription/invoice/checkout/', {
    method: 'POST',
    body: { plan_slug, requisites, offer_accepted },
    headers: wsHeaders(workspaceId),
  });

// GET /invoice/<id>/ → {invoice_id, document_id, number, status, paid, amount_rub, plan_slug, created_at, paid_at, failure_reason}
export const getInvoice = (invoiceId, workspaceId) =>
  apiFetch(`/api/subscription/invoice/${encodeURIComponent(invoiceId)}/`, { headers: wsHeaders(workspaceId) });

// POST /invoice/<id>/email/ → {sent:true}. email опционален (без него — на почту аккаунта).
export const sendInvoiceEmail = (invoiceId, email, workspaceId) =>
  apiFetch(`/api/subscription/invoice/${encodeURIComponent(invoiceId)}/email/`, {
    method: 'POST',
    body: email ? { email } : {},
    headers: wsHeaders(workspaceId),
  });

// GET /payer-requisites/ → {legal_type, inn, kpp, legal_name} — сохранённые реквизиты (префилл).
export const getPayerRequisites = (workspaceId) =>
  apiFetch('/api/subscription/payer-requisites/', { headers: wsHeaders(workspaceId) });

// POST /invoice/<id>/act/email/ → {sent:true} — закрывающий акт (этап 2).
export const sendActEmail = (invoiceId, email, workspaceId) =>
  apiFetch(`/api/subscription/invoice/${encodeURIComponent(invoiceId)}/act/email/`, {
    method: 'POST',
    body: email ? { email } : {},
    headers: wsHeaders(workspaceId),
  });
